import { motion } from "framer-motion";
import SectionHeading from "@/components/SectionHeading";
import { FadeUp, Stagger, StaggerItem, EASE } from "@/components/Reveal";

export const NEURO_NODES = [
  { title: "Atenção", desc: "Como selecionamos e mantemos o foco diante das informações.", pos: [50, 4] },
  { title: "Memória", desc: "Como registramos, armazenamos e recuperamos informações.", pos: [88, 20] },
  { title: "Funções Executivas", desc: "Planejamento, organização, flexibilidade, controle e resolução de problemas.", pos: [94, 56] },
  { title: "Linguagem", desc: "Aspectos relacionados à compreensão e à expressão.", pos: [74, 88] },
  { title: "Aprendizagem", desc: "Aquisição de novos conhecimentos e habilidades.", pos: [26, 88] },
  { title: "Raciocínio", desc: "Análise de informações, relações e resolução de problemas.", pos: [6, 56] },
  { title: "Aspectos Emocionais e Comportamentais", desc: "Fatores considerados na compreensão integrada da pessoa.", pos: [12, 20] },
];

const NeuroMap = () => (
  <section data-testid="neuro-map-section" className="overflow-hidden bg-raiz-900 py-20 text-raiz-50 lg:py-28">
    <div className="container-x">
      <SectionHeading
        eyebrow="Mapa do funcionamento"
        title="O funcionamento humano é integrado."
        lead="Nenhuma habilidade funciona sozinha. A avaliação neuropsicológica observa como essas áreas se relacionam em cada pessoa."
        dark
        testId="neuro-map"
      />

      <div className="relative mx-auto mt-16 hidden aspect-square max-w-3xl lg:block" data-testid="neuro-map-desktop">
        <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden="true">
          {NEURO_NODES.map((n, i) => (
            <motion.path
              key={n.title}
              d={`M50 50 Q ${(50 + n.pos[0]) / 2 + (i % 2 === 0 ? 6 : -6)} ${(50 + n.pos[1]) / 2} ${n.pos[0]} ${n.pos[1]}`}
              fill="none"
              stroke="rgba(203,213,172,0.4)"
              strokeWidth="0.25"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 1.6, delay: 0.2 + i * 0.15, ease: EASE }}
            />
          ))}
        </svg>
        <FadeUp className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <p className="max-w-[180px] rounded-full border border-raiz-200/30 bg-raiz-800/80 px-6 py-8 text-center font-serif text-base italic leading-snug text-raiz-100 backdrop-blur-sm">
            O funcionamento humano é integrado.
          </p>
        </FadeUp>
        {NEURO_NODES.map((n, i) => (
          <motion.div
            key={n.title}
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 0.7, delay: 0.5 + i * 0.15, ease: EASE }}
            className="group absolute w-44 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${n.pos[0]}%`, top: `${n.pos[1]}%` }}
            data-testid={`neuro-node-${i}`}
          >
            <div className="rounded-2xl border border-raiz-50/20 bg-raiz-950/60 p-4 backdrop-blur-sm transition-colors duration-500 group-hover:border-raiz-200/60">
              <p className="text-[13px] font-medium uppercase tracking-[0.14em] text-raiz-200">{n.title}</p>
              <p className="mt-2 text-xs leading-relaxed text-raiz-100/60">{n.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <Stagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:hidden" gap={0.08} data-testid="neuro-map-mobile">
        {NEURO_NODES.map((n, i) => (
          <StaggerItem key={n.title}>
            <div data-testid={`neuro-node-m-${i}`} className="rounded-2xl border border-raiz-50/20 bg-raiz-950/40 p-5">
              <p className="text-[13px] font-medium uppercase tracking-[0.14em] text-raiz-200">{n.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-raiz-100/65">{n.desc}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

export default NeuroMap;
